/**
 * A soft, looping ambient track synthesized with the Web Audio API: a slow
 * Cmaj7 → Am7 → Fmaj7 → G pad with a sine bass and a gentle echoing
 * arpeggio. Nothing is downloaded, so there is no audio file to license.
 */

/** MIDI note numbers for each chord in the progression. */
const CHORDS = [
    [60, 64, 67, 71],
    [57, 60, 64, 67],
    [53, 57, 60, 64],
    [55, 59, 62, 67],
];
const ARPEGGIO_PATTERN = [0, 1, 2, 3, 2, 1, 2, 3];
const CHORD_SECONDS = 4;
const ARPEGGIO_STEP_SECONDS = CHORD_SECONDS / ARPEGGIO_PATTERN.length;
const LOOKAHEAD_SECONDS = 1.5;
const SCHEDULE_INTERVAL_MS = 400;
const MASTER_VOLUME = 0.5;
const FADE_SECONDS = 0.4;

const toFrequency = (midiNote) => 440 * 2 ** ((midiNote - 69) / 12);

export const createAmbientMusic = () => {
    // iOS mutes web audio when the ring/silent switch is on unless the page's
    // audio session is marked as playback (Safari 17+).
    if (navigator.audioSession) {
        navigator.audioSession.type = "playback";
    }

    const context = new AudioContext();

    const master = context.createGain();
    master.gain.value = 0;
    master.connect(context.destination);

    const filter = context.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = 1800;
    filter.connect(master);

    const echo = context.createDelay();
    const echoFeedback = context.createGain();
    echo.delayTime.value = ARPEGGIO_STEP_SECONDS * 1.5;
    echoFeedback.gain.value = 0.3;
    echo.connect(echoFeedback);
    echoFeedback.connect(echo);
    echo.connect(filter);

    let nextChordAt = context.currentTime + 0.1;
    let chordIndex = 0;
    let scheduleTimer = null;
    let suspendTimer = null;

    /**
     * Plays one enveloped note: a linear rise over `attack`, then a linear
     * fall to silence at the end of `duration`.
     */
    const playNote = (
        midiNote,
        startAt,
        duration,
        { type, peak, attack, detune = 0, withEcho = false },
    ) => {
        const oscillator = context.createOscillator();
        const envelope = context.createGain();

        oscillator.type = type;
        oscillator.frequency.value = toFrequency(midiNote);
        oscillator.detune.value = detune;

        envelope.gain.setValueAtTime(0, startAt);
        envelope.gain.linearRampToValueAtTime(peak, startAt + attack);
        envelope.gain.linearRampToValueAtTime(0, startAt + duration);

        oscillator.connect(envelope);
        envelope.connect(filter);

        if (withEcho) {
            envelope.connect(echo);
        }

        oscillator.start(startAt);
        oscillator.stop(startAt + duration + 0.05);
    };

    const scheduleChord = (chord, startAt) => {
        chord.forEach((note) => {
            [-4, 4].forEach((detune) =>
                playNote(note, startAt, CHORD_SECONDS + 0.8, {
                    type: "triangle",
                    peak: 0.025,
                    attack: 1.2,
                    detune,
                }),
            );
        });

        playNote(chord[0] - 12, startAt, CHORD_SECONDS, {
            type: "sine",
            peak: 0.09,
            attack: 0.3,
        });

        ARPEGGIO_PATTERN.forEach((noteIndex, step) =>
            playNote(
                chord[noteIndex] + 12,
                startAt + step * ARPEGGIO_STEP_SECONDS,
                0.6,
                { type: "sine", peak: 0.03, attack: 0.01, withEcho: true },
            ),
        );
    };

    /** Queues chords a little ahead of the audio clock so playback never gaps. */
    const schedule = () => {
        while (nextChordAt < context.currentTime + LOOKAHEAD_SECONDS) {
            scheduleChord(CHORDS[chordIndex], nextChordAt);
            nextChordAt += CHORD_SECONDS;
            chordIndex = (chordIndex + 1) % CHORDS.length;
        }
    };

    const fadeTo = (volume) => {
        master.gain.cancelScheduledValues(context.currentTime);
        master.gain.setValueAtTime(master.gain.value, context.currentTime);
        master.gain.linearRampToValueAtTime(
            volume,
            context.currentTime + FADE_SECONDS,
        );
    };

    return {
        /** Resumes (or starts) the music and fades it in. Call from a user gesture the first time. */
        play() {
            clearTimeout(suspendTimer);
            context.resume();
            fadeTo(MASTER_VOLUME);
            schedule();
            scheduleTimer ??= setInterval(schedule, SCHEDULE_INTERVAL_MS);
        },

        /** Fades the music out, then suspends the audio clock so it picks up where it left off. */
        pause() {
            fadeTo(0);
            clearTimeout(suspendTimer);
            suspendTimer = setTimeout(
                () => context.suspend(),
                FADE_SECONDS * 1000,
            );
        },

        close() {
            clearInterval(scheduleTimer);
            clearTimeout(suspendTimer);
            context.close();
        },
    };
};
